import React, { useState, useEffect, useRef, useMemo } from "react";
import { createPortal } from "react-dom";
import { ChevronLeftIcon, ChevronRightIcon, XMarkIcon } from "@heroicons/react/24/outline";
import { useTranslation } from "react-i18next";
import { fetchUserWorkingDaysCount } from "../../api/user";

/** Format a Date as YYYY-MM-DD using local time (avoids UTC day-shift). */
export const formatDateString = (date) => {
  if (typeof date === "string") {
    return date.substring(0, 10);
  }
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, "0");
  const day = String(date.getDate()).padStart(2, "0");
  return `${year}-${month}-${day}`;
};

/**
 * Pure date-selectability rules used by PMDatePicker (exported for tests).
 * `validationRules.unlimited` skips the calendar-day maxDays cap.
 *
 * @param {Date|string} date
 * @param {Object} validationRules
 * @param {string} [name] field name — date_too + userId skips calendar maxDays
 */
export const evaluateDateSelectable = (date, validationRules = {}, name) => {
  const {
    maxDays,
    minDate,
    maxDate,
    startDate,
    isDateValid,
    userId,
    unlimited,
  } = validationRules || {};

  const dateStr = formatDateString(date);
  const dateObj = new Date(dateStr);

  if (minDate && dateObj < new Date(minDate)) {
    return false;
  }
  if (maxDate && dateObj > new Date(maxDate)) {
    return false;
  }

  // Unlimited types (sick leave, compensatory time off) never apply maxDays.
  if (!unlimited && startDate && maxDays) {
    // When userId is provided for date_too, backend validates working days.
    if (!(userId && name === "date_too")) {
      const start = new Date(startDate);
      const diffTime = Math.abs(dateObj - start);
      const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24)) + 1;
      if (diffDays > maxDays) {
        return false;
      }
    }
  }

  if (isDateValid && typeof isDateValid === "function") {
    return isDateValid(dateStr);
  }

  return true;
};

/**
 * PMDatePicker - Custom date picker component with validation support
 * 
 * Features:
 * - Visual indication of valid/invalid dates
 * - Support for date range selection
 * - Customizable validation rules
 * - Responsive design
 * - Work schedule aware (calculates working days instead of calendar days)
 * 
 * @param {Object} props
 * @param {string} props.value - Selected date value (YYYY-MM-DD)
 * @param {Function} props.onChange - Callback when date changes
 * @param {string} props.label - Label for the date picker
 * @param {string} props.name - Field name
 * @param {Object} props.validationRules - Validation rules object
 * @param {number} props.validationRules.maxDays - Maximum number of working days allowed (uses work_schedule if userId provided)
 * @param {boolean} props.validationRules.unlimited - Skip maxDays (sick leave / compensatory time off)
 * @param {Date} props.validationRules.minDate - Minimum selectable date
 * @param {Date} props.validationRules.maxDate - Maximum selectable date
 * @param {Date} props.validationRules.startDate - Start date for range validation
 * @param {Function} props.validationRules.isDateValid - Custom validation function
 * @param {number} props.validationRules.userId - User ID for work schedule calculation
 * @param {Function} props.validationRules.onWorkingDaysCalculated - Callback when working days are calculated
 * @param {boolean} props.disabled - Whether the picker is disabled
 * @param {string} props.placeholder - Placeholder text
 * @param {Object} props.dayInfoMap - Optional map of date strings (YYYY-MM-DD) to info text shown below day number (e.g. {"2025-03-06": "8t"})
 * @param {Function} props.onOpen - Optional callback when picker opens (e.g. to fetch day info for visible month)
 * @param {Function} props.onMonthChange - Optional callback (month, year) when user navigates to another month (e.g. to fetch day info)
 */
export const PMDatePicker = ({
  value,
  onChange,
  label,
  name,
  validationRules = {},
  disabled = false,
  placeholder = "Velg dato",
  errors,
  touched,
  dayInfoMap = {},
  onOpen,
  onMonthChange,
  inputRef: externalInputRef,
  /** "design" renders the Promang-style trigger (calendar icon + long date text). */
  triggerVariant,
  accentColor = "#1463C7",
  ...other
}) => {
  // Filter out Formik props that must not be spread onto DOM input
  const FORMIK_PROPS = new Set([
    "setTouched", "setValues", "submitForm", "validateForm", "validateField",
    "isValid", "dirty", "unregisterField", "registerField", "getFieldProps",
    "getFieldMeta", "getFieldHelpers", "validateOnBlur", "validateOnChange", "validateOnMount",
  ]);
  const inputProps = Object.fromEntries(
    Object.entries(other).filter(([k]) => !FORMIK_PROPS.has(k))
  );
  const { t } = useTranslation();
  const [isOpen, setIsOpen] = useState(false);
  const [currentMonth, setCurrentMonth] = useState(new Date().getMonth() + 1);
  const [currentYear, setCurrentYear] = useState(new Date().getFullYear());
  const pickerRef = useRef(null);
  const inputRef = useRef(null);
  const setInputElement = (el) => {
    inputRef.current = el;
    if (typeof externalInputRef === "function") {
      externalInputRef(el);
    } else if (externalInputRef && "current" in externalInputRef) {
      externalInputRef.current = el;
    }
  };
  const [position, setPosition] = useState({ top: 0, left: 0 });
  const [forceUpdate, setForceUpdate] = useState(0);
  const [isMobile, setIsMobile] = useState(false);

  const {
    maxDays,
    minDate,
    maxDate,
    startDate,
    isDateValid,
    userId,
    onWorkingDaysCalculated,
    unlimited,
  } = validationRules;
  
  const [workingDaysCount, setWorkingDaysCount] = useState(null);
  const [isCalculatingWorkingDays, setIsCalculatingWorkingDays] = useState(false);

  // Detect mobile device
  useEffect(() => {
    const checkMobile = () => {
      setIsMobile(window.innerWidth < 768); // md breakpoint
    };
    
    checkMobile();
    window.addEventListener('resize', checkMobile);
    
    return () => {
      window.removeEventListener('resize', checkMobile);
    };
  }, []);

  // Prevent body scroll when modal is open on mobile
  useEffect(() => {
    if (isOpen && isMobile) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
    }
    
    return () => {
      document.body.style.overflow = '';
    };
  }, [isOpen, isMobile]);

  // Force re-render when validationRules change
  // This ensures the calendar updates when absence type changes
  useEffect(() => {
    setForceUpdate(prev => prev + 1);
  }, [maxDays, minDate, maxDate, startDate, unlimited]);
  
  // Update when picker opens to ensure latest validation rules are used
  useEffect(() => {
    if (isOpen) {
      setForceUpdate(prev => prev + 1);
    }
  }, [isOpen]);

  // Calculate working days when value and startDate change (if userId is provided)
  useEffect(() => {
    if (userId && startDate && value && name === "date_too") {
      setIsCalculatingWorkingDays(true);
      fetchUserWorkingDaysCount(userId, startDate, value)
        .then((result) => {
          if (result.working_days_count !== undefined) {
            setWorkingDaysCount(result.working_days_count);
            if (onWorkingDaysCalculated) {
              onWorkingDaysCalculated(result.working_days_count);
            }
          }
        })
        .catch((error) => {
          console.error("Error calculating working days:", error);
          setWorkingDaysCount(null);
        })
        .finally(() => {
          setIsCalculatingWorkingDays(false);
        });
    } else {
      setWorkingDaysCount(null);
    }
  }, [userId, startDate, value, name, onWorkingDaysCalculated]);

  // Set initial month/year based on value or startDate
  useEffect(() => {
    if (value) {
      const date = new Date(value);
      setCurrentMonth(date.getMonth() + 1);
      setCurrentYear(date.getFullYear());
    } else if (startDate) {
      const date = new Date(startDate);
      setCurrentMonth(date.getMonth() + 1);
      setCurrentYear(date.getFullYear());
    }
  }, [value, startDate]);

  // Calculate position when picker opens
  useEffect(() => {
    if (isOpen && inputRef.current && !isMobile) {
      const rect = inputRef.current.getBoundingClientRect();
      const pickerWidth = 320; // w-80 = 320px
      const pickerHeight = 400; // Approximate height
      const gap = 4; // 4px gap between input and picker
      
      let top = rect.bottom + gap;
      let left = rect.left;
      
      // Check if picker goes below viewport, if so show above input
      if (top + pickerHeight > window.innerHeight) {
        top = rect.top - pickerHeight - gap;
        // If still doesn't fit above, position at top of viewport
        if (top < 0) {
          top = 8; // 8px from top
        }
      }
      
      // Check if picker goes beyond right edge of viewport
      if (left + pickerWidth > window.innerWidth) {
        left = window.innerWidth - pickerWidth - 8; // 8px margin from right
      }
      
      // Check if picker goes beyond left edge of viewport
      if (left < 0) {
        left = 8; // 8px margin from left
      }
      
      setPosition({
        top,
        left,
      });
    }
  }, [isOpen, isMobile]);

  // Close picker when clicking outside (desktop only, mobile uses overlay)
  useEffect(() => {
    if (!isOpen || isMobile) return;

    const handleClickOutside = (event) => {
      if (
        pickerRef.current &&
        !pickerRef.current.contains(event.target) &&
        inputRef.current &&
        !inputRef.current.contains(event.target)
      ) {
        setIsOpen(false);
      }
    };

    // Use setTimeout to avoid immediate closure
    setTimeout(() => {
      document.addEventListener("mousedown", handleClickOutside);
    }, 0);

    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [isOpen, isMobile]);

  const monthNames = [
    "Januar",
    "Februar",
    "Mars",
    "April",
    "Mai",
    "Juni",
    "Juli",
    "August",
    "September",
    "Oktober",
    "November",
    "Desember",
  ];

  const weekDays = ["Ma", "Ti", "On", "To", "Fr", "Lø", "Sø"];

  const handleMonthChange = (newMonth) => {
    let newYear = currentYear;
    if (newMonth > 12) {
      newMonth = 1;
      newYear = currentYear + 1;
    }
    if (newMonth < 1) {
      newMonth = 12;
      newYear = currentYear - 1;
    }
    setCurrentMonth(newMonth);
    setCurrentYear(newYear);
    onMonthChange?.(newMonth, newYear);
  };

  const isDateSelectable = (date) => evaluateDateSelectable(date, validationRules, name);

  const getDaysForCurrentMonth = () => {
    const days = [];
    const today = new Date();
    const firstDayOfMonth = new Date(currentYear, currentMonth - 1, 1);
    const lastDayOfMonth = new Date(currentYear, currentMonth, 0).getDate();
    const firstDayOfMonthIndex = firstDayOfMonth.getDay();
    // Adjust for Norwegian week (Monday = 0, Sunday = 6)
    // JavaScript getDay() returns 0 for Sunday, 1 for Monday, etc.
    // We want Monday = 0, so we adjust: (day + 6) % 7
    const daysBefore = (firstDayOfMonthIndex + 6) % 7;
    const daysAfter = 42 - (daysBefore + lastDayOfMonth);

    // Previous month days
    const prevMonth = currentMonth === 1 ? 12 : currentMonth - 1;
    const prevYear = currentMonth === 1 ? currentYear - 1 : currentYear;
    const prevMonthLastDay = new Date(prevYear, prevMonth, 0).getDate();

    for (let i = daysBefore - 1; i >= 0; i--) {
      const day = prevMonthLastDay - i;
      const date = new Date(prevYear, prevMonth - 1, day);
      const dateStr = formatDateString(date);
      days.push({
        date: dateStr,
        day,
        isCurrentMonth: false,
        isToday: false,
        isSelected: dateStr === value,
        isValid: isDateSelectable(date),
      });
    }

    // Current month days
    for (let day = 1; day <= lastDayOfMonth; day++) {
      const date = new Date(currentYear, currentMonth - 1, day);
      const dateStr = formatDateString(date);
      const isToday =
        today.getDate() === day &&
        today.getMonth() + 1 === currentMonth &&
        today.getFullYear() === currentYear;
      const isValid = isDateSelectable(date);

      days.push({
        date: dateStr,
        day,
        isCurrentMonth: true,
        isToday,
        isSelected: dateStr === value,
        isValid,
      });
    }

    // Next month days
    const nextMonth = currentMonth === 12 ? 1 : currentMonth + 1;
    const nextYear = currentMonth === 12 ? currentYear + 1 : currentYear;
    for (let day = 1; day <= daysAfter; day++) {
      const date = new Date(nextYear, nextMonth - 1, day);
      const dateStr = formatDateString(date);
      days.push({
        date: dateStr,
        day,
        isCurrentMonth: false,
        isToday: false,
        isSelected: dateStr === value,
        isValid: isDateSelectable(date),
      });
    }

    return days;
  };

  const handleDateSelect = (dateStr) => {
    if (onChange) {
      onChange({
        target: {
          name,
          value: dateStr,
        },
      });
    }
    setIsOpen(false);
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return "";
    const date = new Date(dateStr);
    return date.toLocaleDateString("nb-NO", {
      day: "2-digit",
      month: "2-digit",
      year: "numeric",
    });
  };

  const formatDateLong = (dateStr) => {
    if (!dateStr) return "";
    const date = new Date(`${String(dateStr).substring(0, 10)}T00:00:00`);
    if (Number.isNaN(date.getTime())) return "";
    const s = date.toLocaleDateString("nb-NO", { weekday: "long", day: "numeric", month: "long" });
    return s.charAt(0).toUpperCase() + s.slice(1);
  };

  // Recalculate days when validation rules change or month/year changes
  const days = useMemo(() => {
    return getDaysForCurrentMonth();
  }, [currentMonth, currentYear, value, maxDays, minDate, maxDate, startDate, isDateValid, unlimited, forceUpdate]);

  return (
    <div className="relative" ref={pickerRef}>
      {label && (
        <label
          htmlFor={name}
          className="block text-sm font-medium text-gray-700 mb-1"
        >
          {label}
        </label>
      )}
      <div className="relative">
        {triggerVariant === "design" ? (
          <button
            ref={setInputElement}
            type="button"
            name={name}
            disabled={disabled}
            onClick={() => {
              if (disabled) return;
              if (!isOpen) onOpen?.();
              setIsOpen(!isOpen);
            }}
            style={{
              width: "100%",
              display: "flex",
              alignItems: "center",
              gap: 12,
              padding: "14px 16px",
              border: `1.5px solid ${errors && touched ? "#FCA5A5" : "#E2E8F0"}`,
              borderRadius: 14,
              background: disabled ? "#F1F5F9" : "#fff",
              cursor: disabled ? "not-allowed" : "pointer",
              fontFamily: "inherit",
              textAlign: "left",
            }}
            {...inputProps}
          >
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke={accentColor} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <rect x="3" y="4" width="18" height="18" rx="2" />
              <path d="M16 2v4M8 2v4M3 10h18" />
            </svg>
            <span style={{ flex: 1, fontSize: 16, fontWeight: 600, color: value ? "#0F172A" : "#94A3B8", textTransform: "capitalize" }}>
              {value ? formatDateLong(value) : placeholder}
            </span>
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="#94A3B8" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <path d="M6 9l6 6 6-6" />
            </svg>
          </button>
        ) : (
        <input
          ref={setInputElement}
          type="text"
          name={name}
          value={value ? formatDate(value) : ""}
          placeholder={placeholder}
          readOnly
          disabled={disabled}
          onClick={() => {
            if (disabled) return;
            if (!isOpen) onOpen?.();
            setIsOpen(!isOpen);
          }}
          className={`block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 sm:text-sm ${
            errors && touched
              ? "border-red-300 focus:border-red-500 focus:ring-red-500"
              : ""
          } ${disabled ? "bg-gray-100 cursor-not-allowed" : "cursor-pointer"}`}
          {...inputProps}
        />
        )}
        {isOpen && !disabled && createPortal(
          <>
            {/* Mobile: Full-screen overlay */}
            {isMobile && (
              <div
                className="fixed inset-0 bg-black bg-opacity-50 z-[99998]"
                onClick={() => setIsOpen(false)}
              />
            )}
            
            {/* Calendar Container */}
            <div
              ref={pickerRef}
              className={`
                ${isMobile 
                  ? 'fixed inset-0 z-[99999] flex items-center justify-center p-4' 
                  : 'fixed z-[99999] bg-white rounded-lg shadow-xl border border-gray-200 p-4 w-80'
                }
              `}
              style={!isMobile ? {
                top: `${position.top}px`,
                left: `${position.left}px`,
              } : {}}
              onClick={(e) => e.stopPropagation()}
            >
              <div className={`
                ${isMobile 
                  ? 'bg-white rounded-lg shadow-xl w-full max-w-sm mx-auto max-h-[90vh] overflow-y-auto' 
                  : 'w-full'
                }
              `}>
                {/* Mobile: Close button */}
                {isMobile && (
                  <div className="flex justify-end p-4 pb-2 border-b border-gray-200">
                    <button
                      type="button"
                      onClick={() => setIsOpen(false)}
                      className="p-2 rounded-md text-gray-400 hover:text-gray-600 hover:bg-gray-100"
                      aria-label="Lukk"
                    >
                      <XMarkIcon className="h-6 w-6" />
                    </button>
                  </div>
                )}

                <div className={isMobile ? 'p-4' : ''}>
                  {/* Month/Year Navigation */}
                  <div className="flex items-center justify-between mb-4">
                    <button
                      type="button"
                      onClick={() => handleMonthChange(currentMonth - 1)}
                      className={`p-1 rounded-md text-gray-400 hover:text-gray-600 hover:bg-gray-100 ${isMobile ? 'p-2' : ''}`}
                    >
                      <ChevronLeftIcon className={isMobile ? "h-6 w-6" : "h-5 w-5"} />
                    </button>
                    <div className={`font-semibold text-gray-900 ${isMobile ? 'text-lg' : 'text-sm'}`}>
                      {monthNames[currentMonth - 1]} {currentYear}
                    </div>
                    <button
                      type="button"
                      onClick={() => handleMonthChange(currentMonth + 1)}
                      className={`p-1 rounded-md text-gray-400 hover:text-gray-600 hover:bg-gray-100 ${isMobile ? 'p-2' : ''}`}
                    >
                      <ChevronRightIcon className={isMobile ? "h-6 w-6" : "h-5 w-5"} />
                    </button>
                  </div>

                  {/* Week Days Header */}
                  <div className="grid grid-cols-7 gap-1 mb-2">
                    {weekDays.map((day) => (
                      <div
                        key={day}
                        className={`text-center font-medium text-gray-500 py-1 ${isMobile ? 'text-sm' : 'text-xs'}`}
                      >
                        {day}
                      </div>
                    ))}
                  </div>

                  {/* Calendar Grid */}
                  <div className="grid grid-cols-7 gap-1" key={`calendar-${forceUpdate}-${maxDays}-${startDate}`}>
                    {days.map((day, idx) => {
                      const isSelectable = day.isCurrentMonth && day.isValid;
                      const isDisabled = day.isCurrentMonth && !day.isValid;
                      const dayInfo = dayInfoMap?.[day.date];

                      return (
                        <button
                          key={`${day.date}-${idx}`}
                          type="button"
                          onClick={() => isSelectable && handleDateSelect(day.date)}
                          disabled={!isSelectable}
                          className={`
                            ${isMobile ? 'h-12 w-12 min-h-12 min-w-12' : 'h-10 w-10 min-h-10 min-w-10'} 
                            flex flex-col items-center justify-center rounded-md transition-colors text-sm
                            ${
                              !day.isCurrentMonth
                                ? "text-gray-300 cursor-default"
                                : isDisabled
                                ? "text-gray-300 bg-gray-50 cursor-not-allowed"
                                : day.isSelected
                                ? "bg-blue-600 text-white font-semibold"
                                : day.isToday
                                ? "bg-blue-100 text-blue-600 font-semibold hover:bg-blue-200"
                                : day.isValid
                                ? "bg-green-50 text-green-700 hover:bg-green-100 font-medium"
                                : "text-gray-400 bg-gray-50 cursor-not-allowed"
                            }
                          `}
                          title={
                            isDisabled
                              ? "Denne datoen er ikke tillatt"
                              : day.isValid
                              ? dayInfo ? `${day.day}. ${dayInfo}` : "Klikk for å velge"
                              : ""
                          }
                        >
                          <span className="leading-tight">{day.day}</span>
                          {dayInfo && (
                            <span className={`text-[10px] leading-tight mt-0.5 ${
                              day.isSelected ? "text-blue-100" : day.isToday ? "text-blue-500" : "text-gray-500"
                            }`}>
                              {dayInfo}
                            </span>
                          )}
                        </button>
                      );
                    })}
                  </div>

                  {/* Validation Info */}
                  {maxDays && startDate && !unlimited && (
                    <div className="mt-3 pt-3 border-t border-gray-200">
                      <p className={`text-gray-500 text-center ${isMobile ? 'text-sm' : 'text-xs'}`}>
                        Maksimalt {maxDays} {maxDays === 1 ? "arbeidsdag" : "arbeidsdager"} fra startdato
                        {userId && name === "date_too" && workingDaysCount !== null && value && (
                          <span className="block mt-1 font-semibold">
                            {isCalculatingWorkingDays ? (
                              "Beregner..."
                            ) : (
                              `Valgt: ${workingDaysCount} ${workingDaysCount === 1 ? "arbeidsdag" : "arbeidsdager"}`
                            )}
                          </span>
                        )}
                      </p>
                    </div>
                  )}
                </div>
              </div>
            </div>
          </>,
          document.body
        )}
      </div>
      {errors && touched && (
        <p className="mt-1 text-sm text-red-600">
          {typeof errors === "string" || typeof errors === "number"
            ? String(errors)
            : Array.isArray(errors)
              ? errors.join(", ")
              : ""}
        </p>
      )}
    </div>
  );
};

